// src/context/NotesContext.js
import React, { createContext, useContext, useState } from "react";
import { fetchWithAuth } from "./AuthService";
import { decryptWithUserKey, encryptWithUserKey } from "./EncryptNotes";
import { useAuth } from "./AuthContext";

const NotesContext = createContext();

export const NotesProvider = ({ children }) => {
  const { userKey, setUserKey } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notesHash, setNotesHash] = useState(null);

  const fetchUserKey = async () => {
    const res = await fetchWithAuth('/api/notes/userkey');
    if (!res.ok) throw new Error('Impossible de récupérer la userKey');
    const data = await res.json();
    setUserKey(data.userKey); // update dans AuthContext
    return data.userKey;
  };

  const getKey = async () => {
    if (!userKey) return await fetchUserKey();
    return userKey;
  };

  const checkAndLoadNotes = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const hashRes = await fetchWithAuth("/api/notes/hash?log=true");
      const { hash } = await hashRes.json();

      let key = await getKey();
      if (!key) throw new Error("Impossible de récupérer la clé utilisateur pour déchiffrer les notes");
        
      // Si on n’a pas encore de hash ou qu’il est différent → recharger     
      if (!notesHash || hash !== notesHash) {
        const res = await fetchWithAuth("/api/notes/all");
        const data = await res.json();
                
        let decryptedNotes = [];
        if (data.notes && Array.isArray(data.notes)) {
          decryptedNotes = await Promise.all(
            data.notes.map(async (note) => {
              try {
                const title = await decryptWithUserKey(note.title, key);
                const text = await decryptWithUserKey(note.text, key);
                return { ...note, title, text };
              } catch (err) {
                console.error("Erreur de décryptage pour la note :", note._id, err);
                return note;
              }
            })
          );
        }

        setNotes(decryptedNotes || []);
        setNotesHash(hash);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des notes :", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshNotesHash = async () => {
    if (loading) return; // bloque si déjà en cours
    setLoading(true);
    try {
      const hashRes = await fetchWithAuth("/api/notes/hash");
      const { hash } = await hashRes.json();
      if (notes.length > 0) setNotesHash(hash); // seulement si des notes
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const saveNote = async (note) => {
    const key = await getKey();

    const encryptedTitle = await encryptWithUserKey(note.title || "", key);
    const encryptedText = await encryptWithUserKey(note.text || "", key);

    const body = {
      ...note,
      title: encryptedTitle,
      text: encryptedText,
    };

    let res;
    if (note._id) {
      res = await fetchWithAuth(`/api/notes/${note._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } else {
      res = await fetchWithAuth(`/api/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    }

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Erreur serveur");

    const noteForContext = {
      ...data.note,
      title: note.title,
      text: note.text,
    };

    if (note._id) updateNote(noteForContext);
    else addNote(noteForContext);

    return noteForContext;
  };

  const addNote = (newNote) => {
    setNotes((prev) => {
      const updated = [newNote, ...prev];
      const unique = Array.from(new Map(updated.map((n) => [n._id, n])).values());
      return unique;
    });
  };

  const updateNote = (updatedNote) => {
    setNotes((prev) =>
      prev.map((n) => (n._id === updatedNote._id ? updatedNote : n))
    );
  };

  const deleteNote = (noteId) => {
    setNotes((prev) => prev.filter((n) => n._id !== noteId));
  };

  const getNotesByScanId = (scanId) => {
    if (!scanId) return [];
    return notes.filter(note => note.scanResultId === scanId);
  };

  return (
    <NotesContext.Provider
      value={{
        notes,
        loading,
        notesHash,
        getKey,
        checkAndLoadNotes,
        addNote,
        updateNote,
        deleteNote,
        refreshNotesHash,
        saveNote,
        getNotesByScanId
      }}
    >
      {children}
    </NotesContext.Provider>
  );
};

export const useNotes = () => useContext(NotesContext);
